import { roundToDecimal } from "../utils/math";

export interface Budget503020Result {
  needs: number;
  wants: number;
  savings: number;
}

/**
 * 50/30/20 Rule Budgeting
 */
export function budget503020(monthlyNetIncome: number): Budget503020Result {
  const income = Math.max(0, monthlyNetIncome);
  return {
    needs: roundToDecimal(income * 0.50, 2),
    wants: roundToDecimal(income * 0.30, 2),
    savings: roundToDecimal(income * 0.20, 2),
  };
}

/**
 * Debt-To-Income (DTI) ratio
 */
export function debtToIncome(monthlyDebts: number, monthlyGrossIncome: number): { dtiRatio: number } {
  if (monthlyGrossIncome <= 0) return { dtiRatio: 0 };
  return { dtiRatio: roundToDecimal((monthlyDebts / monthlyGrossIncome) * 100, 2) };
}

/**
 * Net Worth Calculator
 */
export function netWorth(totalAssets: number, totalLiabilities: number): { netWorth: number } {
  return { netWorth: roundToDecimal(totalAssets - totalLiabilities, 2) };
}

/**
 * APRA DTI Cap Check Calculator (Australia, 2026)
 * Checks if a borrower's DTI exceeds 6x and whether the loan falls in the 20% cap.
 */
export function calculateApraDtiCapCheck(
  annualIncome: number,
  currentTotalDebt: number,
  newLoanAmount: number,
  existingMonthlyDebtPayments: number
): {
  dtiRatio: number;
  dtiMultiplier: number;
  exceedsThreshold: boolean;
  totalDebtAfterLoan: number;
  monthlyDebtPaymentsAfterLoan: number;
  estimatedMaxBorrowing: number;
} {
  const grossIncome = Math.max(1, Math.abs(annualIncome));
  const currentDebt = Math.max(0, Math.abs(currentTotalDebt));
  const newLoan = Math.max(0, Math.abs(newLoanAmount));
  const monthlyPayments = Math.max(0, Math.abs(existingMonthlyDebtPayments));

  const totalDebtAfterLoan = currentDebt + newLoan;
  const dtiMultiplier = totalDebtAfterLoan / grossIncome;
  const dtiRatio = dtiMultiplier * 100;
  const exceedsThreshold = dtiMultiplier >= 6;

  const estimatedMonthlyPaymentOnNewLoan = newLoan * 0.00639; // P&I at 6.6% over 30yr: $1,277.40/mo per $200k
  const totalDebtPayments = monthlyPayments + estimatedMonthlyPaymentOnNewLoan;
  const estimatedMaxBorrowing = grossIncome * 6;

  return {
    dtiRatio: roundToDecimal(dtiRatio, 2),
    dtiMultiplier: roundToDecimal(dtiMultiplier, 2),
    exceedsThreshold,
    totalDebtAfterLoan: roundToDecimal(totalDebtAfterLoan, 2),
    monthlyDebtPaymentsAfterLoan: roundToDecimal(totalDebtPayments, 2),
    estimatedMaxBorrowing: roundToDecimal(estimatedMaxBorrowing, 2),
  };
}
